/**
 * Единый набор юридических документов экосистемы (доп. владельца: Legal Center
 * живёт в IEC ID, по одному файлу на тип, одна редакция на все сервисы).
 *
 * `requiresAcceptance` — документ, без принятия которого нельзя пользоваться
 * экосистемой; такие показываются в онбординге и при выходе новой версии.
 *
 * **Этот список — реестр КЛЮЧЕЙ и дефолтов для сида, а не источник истины по
 * `requiresAcceptance` (В9).** В рантайме признак читается из
 * `legal_documents.requires_acceptance`: юрист может сделать документ
 * обязательным новой публикацией, и релиз кода ради этого не выпускается.
 * Сверять состояние с константой — значит завести второй источник правды,
 * который разъедется с БД молча.
 */
export type LegalDocumentKey =
	| "terms-of-use"
	| "privacy-policy"
	| "points-programme-rules"
	| "subscription-terms"
	| "cookies"
	| "cross-border-transfer";

export type LegalDocumentMeta = {
	readonly key: LegalDocumentKey;
	readonly icon: string;
	readonly requiresAcceptance: boolean;
	/** Порядок в списке Legal Center и в онбординге. */
	readonly order: number;
};

export const LEGAL_DOCUMENTS = [
	{ key: "terms-of-use", icon: "Scroll", requiresAcceptance: true, order: 1 },
	{ key: "privacy-policy", icon: "ShieldCheck", requiresAcceptance: true, order: 2 },
	{ key: "points-programme-rules", icon: "Coins", requiresAcceptance: true, order: 3 },
	{ key: "subscription-terms", icon: "CreditCard", requiresAcceptance: false, order: 4 },
	{ key: "cookies", icon: "Cookie", requiresAcceptance: false, order: 5 },
	// Ключ и сид остаются: собранные согласия append-only. В списках Legal
	// Center и публичном манифесте документ не показываем — отдельного дока
	// решили не делать; чем закрыть ст. 16 закона РК, решит юрист.
	{ key: "cross-border-transfer", icon: "Globe", requiresAcceptance: false, order: 6 },
] as const satisfies readonly LegalDocumentMeta[];

/** Не показываем в Legal Center и в гостевом `/legal`. Прямой URL и админка живы. */
export const UNLISTED_LEGAL_DOCUMENT_KEYS = ["cross-border-transfer"] as const;

export function isListedLegalDocument(key: string): boolean {
	return !(UNLISTED_LEGAL_DOCUMENT_KEYS as readonly string[]).includes(key);
}

/**
 * Язык юридических текстов — **только английский** (декрет Д10).
 *
 * Перевод юридического текста — это его новая редакция, а не то же самое
 * другими словами: расхождение перевода с оригиналом означало бы, что принят
 * текст, которого юрист не писал. Поэтому в `legal_document_versions` живёт
 * ровно одна локаль, а язык интерфейса вокруг документа (заголовки разделов,
 * кнопки, подписи в списке) идёт обычным i18n.
 *
 * Отсюда же следует, что `?locale=` публичного API — не выбор языка текста, а
 * лишь пожелание: ответ всегда несёт `resolvedLocale: "en"`, чтобы клиент знал,
 * что показывает английский, и подписал это в интерфейсе.
 */
export const LEGAL_LOCALE = "en" as const;

export type LegalLocale = typeof LEGAL_LOCALE;

/**
 * Необязательное согласие — отзывается в любой момент, сервисы продолжают
 * работать. Оно ОДНО (решение владельца 23.08.2026).
 *
 * Раньше их было три — письма о продукте, персональные рекомендации, новости и
 * предложения. Три галочки описывали одно и то же разрешение («присылайте мне
 * маркетинг») с разных сторон: человек не выбирал между ними осмысленно, а
 * механически ставил все три ради Баллов, и любая комбинация выглядела для нас
 * одинаково. Отдельные основания по ст. 6(1)(a) имеют смысл, когда за ними
 * стоят разные обработки; у нас стояла одна.
 */
export const OPTIONAL_CONSENTS = ["news-and-offers"] as const;

/**
 * Ключи, которые мы БОЛЬШЕ НЕ ПРЕДЛАГАЕМ, но которые есть в истории.
 *
 * Удалить их из типа нельзя: `optional_consents` — append-only журнал (0032),
 * доказательство правового основания по ст. 7(1), и записи с этими ключами
 * останутся навсегда. Выгрузка данных и история согласий обязаны уметь их
 * назвать; предложить их заново нельзя нигде.
 */
export const RETIRED_OPTIONAL_CONSENTS = ["product-emails", "recommendations"] as const;

/** Всё, что может встретиться в журнале: предлагаемое плюс историческое. */
export const ALL_OPTIONAL_CONSENTS = [...OPTIONAL_CONSENTS, ...RETIRED_OPTIONAL_CONSENTS] as const;

/** Ключ, который можно предложить сегодня. */
export type OfferedOptionalConsentKey = (typeof OPTIONAL_CONSENTS)[number];

/** Любой ключ журнала, включая исторические. */
export type OptionalConsentKey = (typeof ALL_OPTIONAL_CONSENTS)[number];

/**
 * Категории cookie и локальных хранилищ (GDPR ст. 6(1)(a), ePrivacy).
 *
 * Категории — ТОЛЬКО те, что мы действительно различаем и учитываем.
 * «Маркетинговых» / рекламных cookie у IEC ID нет, поэтому и категории такой
 * нет: тумблер, за которым ничего не стоит, — это ложь в интерфейсе и лишнее
 * обещание перед регулятором.
 *
 * `configurable: false` — категория, без которой сервис не работает
 * (cookie сессии, `state`/CSRF OAuth-флоу, счётчики антифрода и rate-limit).
 * Согласие на неё не спрашивается: это не выбор, а условие оказания услуги.
 *
 * `defaultEnabled` — состояние тумблера в подробной настройке баннера, а не
 * действующее согласие. До решения человека **ни одна** настраиваемая
 * категория не считается разрешённой (`decided === false` → всё выключено).
 */
export type CookieCategoryMeta = {
	readonly key: string;
	/** Человек может включить/выключить категорию. */
	readonly configurable: boolean;
	/** Стартовое положение тумблера в подробной настройке. */
	readonly defaultEnabled: boolean;
	readonly order: number;
};

// Ни один настраиваемый тумблер не приходит включённым: предустановленная
// галочка согласием не является (Planet49, C-673/17). Стартовое положение
// совпадает с действующим состоянием до решения человека — так интерфейс не
// обещает того, чего в реестре нет. Вопрос «можно ли считать `preferences`
// строго необходимой и вовсе убрать тумблер» — к юристу (LAUNCH-REGISTRY).
export const COOKIE_CATEGORIES = [
	{ key: "necessary", configurable: false, defaultEnabled: true, order: 1 },
	{ key: "preferences", configurable: true, defaultEnabled: false, order: 2 },
	{ key: "analytics", configurable: true, defaultEnabled: false, order: 3 },
] as const satisfies readonly CookieCategoryMeta[];

export type CookieCategoryKey = (typeof COOKIE_CATEGORIES)[number]["key"];

/** Тот же набор кортежем — из него строится `z.enum` контракта. */
export const COOKIE_CATEGORY_KEYS = [
	"necessary",
	"preferences",
	"analytics",
] as const satisfies readonly CookieCategoryKey[];

/** Настраиваемые категории — ровно то, что попадает в реестр согласий. */
export const CONFIGURABLE_COOKIE_CATEGORIES = [
	"preferences",
	"analytics",
] as const satisfies readonly CookieCategoryKey[];

export type ConfigurableCookieCategoryKey = (typeof CONFIGURABLE_COOKIE_CATEGORIES)[number];

/** Настраивается ли категория. Строго необходимая — нет: это не выбор. */
export function isConfigurableCookieCategory(key: string): key is ConfigurableCookieCategoryKey {
	return (CONFIGURABLE_COOKIE_CATEGORIES as readonly string[]).includes(key);
}

/** Снимок решения: настраиваемая категория → разрешена ли. */
export type CookieCategoryChoices = Record<ConfigurableCookieCategoryKey, boolean>;

/** Документ Legal Center, редакция которого фиксируется вместе с выбором. */
export const COOKIE_DOCUMENT_KEY = "cookies" as const satisfies LegalDocumentKey;

/**
 * Гостевой выбор до входа. Записывать его некуда, кроме браузера, поэтому
 * ключ общий у обоих фронтов: `sso.iec.exchange` и `id.iec.exchange` — один
 * origin-домен `*.iec.exchange`, но разные origin'ы, и каждый хранит своё.
 * После входа выбор переносится в реестр, и источником истины становится он.
 */
export const COOKIE_CONSENT_STORAGE_KEY = "iec-id.cookie-consent";

/**
 * Ключи браузерного хранилища, которые живут по согласию «Предпочтения».
 * Отзыв согласия обязан их стереть — иначе тумблер стал бы декорацией.
 */
export const PREFERENCE_STORAGE_KEYS = ["iec-id.locale", "iec-id.theme", "iec-id.ui"] as const;

/** Категория до решения человека выключена — кроме строго необходимой. */
export function defaultCookieChoices(): CookieCategoryChoices {
	return { preferences: false, analytics: false };
}

/** Положение тумблеров в подробной настройке — до того, как выбор сделан. */
export function initialCookieChoices(): CookieCategoryChoices {
	return {
		preferences: cookieCategoryMeta("preferences").defaultEnabled,
		analytics: cookieCategoryMeta("analytics").defaultEnabled,
	};
}

/** Все настраиваемые категории разрешены — кнопка «Принять все». */
export function allCookieChoices(): CookieCategoryChoices {
	return { preferences: true, analytics: true };
}

export function cookieCategoryMeta(key: CookieCategoryKey): CookieCategoryMeta {
	const meta = COOKIE_CATEGORIES.find((category) => category.key === key);
	if (!meta) throw new Error(`Неизвестная категория cookie: ${key}`);
	return meta;
}
